import { Component, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Assistant } from 'src/app/models/core/assistant.model';
import { PromotionalEvent } from 'src/app/models/core/event.model';
import { Product } from 'src/app/models/core/product.model';
import { User } from 'src/app/models/core/user.model';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { ProductApiService } from 'src/app/services/product/product-api.service';
import { environment } from 'src/environments/environment';
import { FormControl } from '@angular/forms';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-share-birthday-message-modal',
  templateUrl: './share-birthday-message-modal.component.html',
  styleUrls: ['./share-birthday-message-modal.component.scss'],
  standalone: false
})
export class ShareBirthdayMessageModalComponent {
  @Input() assistant!: Assistant;
  @Input() promotionalEvents: PromotionalEvent[] = [];
  @Input() sellerCode!: string;
  @Input() isBirthday: boolean = false;
  @Input() seller!: User;
  public referenceControl = new FormControl('');

  public showSelectors = false;
  public selectedEvent: PromotionalEvent | null = null;
  public promotionalProducts: Product[] = [];
  public selectedProduct: Product | null = null;
  public loadingProducts = false;

  // writing effect
  public animatedLink = '';
  private typingInterval: any;

  public platform = Capacitor.getPlatform();

  constructor(
    private modalCtrl: ModalController,
    private customToast: CustomToastService,
    private productApiService: ProductApiService,
  ) {
    // listen to changes in the manual input
    this.referenceControl.valueChanges.subscribe(value => {
      if (value && value.trim()) {
        this.startTypingEffect();
        this.customToast.show('Link agregado manualmente', 2000, 'info');
      }
    });
  }

  get baseMessage(): string {
    const nombre = this.assistant.fullname;
    const vendedor = this.seller?.fullname ?? 'Nuestro equipo';

    if (this.isBirthday) {
      return `¡Hola ${nombre}! Soy ${vendedor} 🥳 \nSabemos que andás de cumple por estos días. Este finde tenemos algo especial para vos 🎁`;
    } else {
      return `¡Hola, ${nombre}! Soy ${vendedor} 👋 \nQueremos volver a verte. Te invitamos a sumarte este finde 🎁`;
    }
  }

  get modalTitle(): string {
    return this.isBirthday
      ? "share-birthday-message-modal.title"
      : "share-birthday-message-modal.title-general";
  }

  get ticketLink(): string {
    return this.getCurrentLink() || '';
  }

  get fullShareMessage(): string {
    const link = this.getCurrentLink();
    if (link) {
      return `${this.baseMessage}\n👉 ${link}`;
    }
    return `${this.baseMessage}\n👉 [Link a compartir]`;
  }

  private getCurrentLink(): string {
    // Priority: manual link
    const manualLink = this.referenceControl.value?.trim();
    if (manualLink) {
      return manualLink;
    }

    if (this.selectedProduct && this.sellerCode) {
      return `${environment.ecommerceDomain}/buy-product/${this.selectedProduct.uuid}?code=${this.sellerCode}`;
    }

    return '';
  }

  toggleSelectors() {
    this.showSelectors = !this.showSelectors;
    if (!this.showSelectors) {
      this.selectedEvent = null;
      this.selectedProduct = null;
      this.promotionalProducts = [];
    }
  }

  onEventSelectedById(eventId: number) {
    if (eventId && typeof eventId === 'number') {
      this.selectedEvent = this.promotionalEvents.find(e => e.id === eventId) || null;
      this.loadPromotionalProducts(eventId);
      this.selectedProduct = null;
    } else {
      this.selectedEvent = null;
      this.selectedProduct = null;
      this.promotionalProducts = [];
    }
  }

  onProductSelectedById(productId: number) {
    if (productId && typeof productId === 'number') {
      this.selectedProduct = this.promotionalProducts.find(p => p.id === productId) || null;
      this.referenceControl.setValue('');
      const generatedLink = this.getCurrentLink();
      this.referenceControl.setValue(generatedLink);
      this.startTypingEffect();
      this.showSelectors = false;
      this.customToast.show('Link generado automáticamente', 3000, 'success');
    } else {
      this.selectedProduct = null;
    }
  }

  private loadPromotionalProducts(eventId: number) {
    this.loadingProducts = true;
    this.productApiService.getPromotionalProducts(eventId).subscribe({
      next: (response) => {
        this.promotionalProducts = response.data;
        this.loadingProducts = false;
      },
      error: (error) => {
        console.error('Error loading promotional products:', error);
        this.customToast.show('Error al cargar los productos promocionales', 3000, 'error');
        this.loadingProducts = false;
      }
    });
  }

  async sendMessage() {
    const currentLink = this.getCurrentLink();
    if (!currentLink || currentLink.trim() === '') {
      this.customToast.show('Por favor ingresa un link para compartir', 3000, 'error');
      return;
    }

    if (!this.isValidUrl(currentLink)) {
      this.customToast.show('Por favor ingresa un link válido', 3000, 'error');
      return;
    }

    try {
      const whatsappUrl = `https://wa.me/${this.assistant.phone}?text=${encodeURIComponent(this.fullShareMessage)}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Error al abrir WhatsApp:', error);
    }
  }

  public async closeModal() {
    await this.modalCtrl.dismiss();
  }

  private isValidUrl(string: string): boolean {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  }

  goToWhatsapp() {
    const whatsappUrl = `https://wa.me/${this.assistant.phone}`;
    window.open(whatsappUrl, '_blank');
  }

  startTypingEffect() {
    const fullText = this.ticketLink;
    if (!fullText) return;

    this.animatedLink = '';
    clearInterval(this.typingInterval);

    let i = 0;
    this.typingInterval = setInterval(() => {
      this.animatedLink += fullText[i];
      i++;
      if (i >= fullText.length) {
        clearInterval(this.typingInterval);
      }
    }, 15);
  }
}
